import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { isLabelKeyValid } from '../../utils';

interface LabelsContextProviderProps {
  children: ReactNode;
}

enum ActionTypes {
  LOAD = 'LOAD',
  UPDATE_KEY = 'UPDATE_KEY',
  UPDATE_VALUE = 'UPDATE_VALUE',
  ADD = 'ADD',
  DELETE = 'DELETE',
  VALIDATE = 'VALIDATE',
  RESET = 'RESET',
}

type LoadLabelAction = {
  type: ActionTypes.LOAD;
  payload: Record<string, string> | undefined;
};

type UpdateKeyLabelAction = {
  type: ActionTypes.UPDATE_KEY;
  payload: {
    id: string;
    key: string;
  };
};

type UpdateValueLabelAction = {
  type: ActionTypes.UPDATE_VALUE;
  payload: {
    id: string;
    value: string;
  };
};

type AddLabelAction = {
  type: ActionTypes.ADD;
};

type DeleteLabelAction = {
  type: ActionTypes.DELETE;
  payload: string;
};

type ValidateAction = {
  type: ActionTypes.VALIDATE;
};

type ResetAction = {
  type: ActionTypes.RESET;
};

type LabelsAction =
  | LoadLabelAction
  | UpdateKeyLabelAction
  | UpdateValueLabelAction
  | AddLabelAction
  | DeleteLabelAction
  | ValidateAction
  | ResetAction;

type LabelsState = {
  labels: {
    id: string;
    key: string;
    value: string;
  }[];
  isKeyInvalidFormat: boolean;
  isDuplicated: boolean;
  isValueEmpty: boolean;
};

type LabelsContextType = {
  labels: {
    id: string;
    key: string;
    value: string;
  }[];
  isKeyInvalidFormat: boolean;
  isDuplicated: boolean;
  isValueEmpty: boolean;
  loadLabels(labels: Record<string, string> | undefined): void;
  updateLabelKey(id: string, key: string): void;
  updateLabelValue(id: string, value: string): void;
  addLabel(): void;
  deleteLabel(id: string): void;
  resetLabels(): void;
};

const initialLabelsState: LabelsState = {
  labels: [],
  isKeyInvalidFormat: false,
  isDuplicated: false,
  isValueEmpty: false,
};

export const LabelsContext = createContext<LabelsContextType | null>(null);

export function LabelsContextProvider(
  props: Readonly<LabelsContextProviderProps>,
) {
  const mapRecordToLabelsArray = (
    labelsRecord: Record<string, string> | undefined,
  ) => {
    if (!labelsRecord) return [];

    return Object.entries(labelsRecord).map(([key, value]) => {
      return {
        id: crypto.randomUUID().toString(),
        key: key,
        value: value,
      };
    });
  };

  const labelsReducer = (
    state: LabelsState,
    action: LabelsAction,
  ): LabelsState => {
    switch (action.type) {
      case ActionTypes.LOAD: {
        const stateCopy = { ...state };
        stateCopy.labels = mapRecordToLabelsArray(action.payload);
        return stateCopy;
      }
      case ActionTypes.UPDATE_KEY: {
        const stateCopy = { ...state };
        stateCopy.labels = stateCopy.labels.map(label =>
          label.id === action.payload.id
            ? { ...label, key: action.payload.key }
            : label,
        );
        return stateCopy;
      }
      case ActionTypes.UPDATE_VALUE: {
        const stateCopy = { ...state };
        stateCopy.labels = stateCopy.labels.map(label =>
          label.id === action.payload.id
            ? { ...label, value: action.payload.value }
            : label,
        );
        return stateCopy;
      }
      case ActionTypes.ADD: {
        const stateCopy = { ...state };
        stateCopy.labels = [
          ...stateCopy.labels,
          { id: crypto.randomUUID().toString(), key: '', value: '' },
        ];
        return stateCopy;
      }
      case ActionTypes.DELETE: {
        const stateCopy = { ...state };
        stateCopy.labels = stateCopy.labels.filter(
          label => label.id !== action.payload,
        );
        return stateCopy;
      }
      case ActionTypes.VALIDATE: {
        const stateCopy = { ...state };

        const keysArray = stateCopy.labels.map(label => label.key);
        stateCopy.isKeyInvalidFormat = keysArray.some(
          key => !isLabelKeyValid(key),
        );
        stateCopy.isDuplicated = keysArray.some(
          (key, index) => keysArray.indexOf(key) !== index,
        );

        const valuesArray = stateCopy.labels.map(label => label.value);
        stateCopy.isValueEmpty = valuesArray.includes('');

        return stateCopy;
      }
      case ActionTypes.RESET: {
        const stateCopy = { ...state };
        stateCopy.labels = [];
        stateCopy.isKeyInvalidFormat = false;
        stateCopy.isDuplicated = false;
        stateCopy.isValueEmpty = false;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(labelsReducer, initialLabelsState);

  useEffect(
    () => dispatch({ type: ActionTypes.VALIDATE }),
    [state.labels, dispatch],
  );

  const loadLabels = (labels: Record<string, string> | undefined) =>
    dispatch({ type: ActionTypes.LOAD, payload: labels });
  const updateLabelKey = (id: string, key: string) =>
    dispatch({ type: ActionTypes.UPDATE_KEY, payload: { id, key } });
  const updateLabelValue = (id: string, value: string) =>
    dispatch({ type: ActionTypes.UPDATE_VALUE, payload: { id, value } });
  const addLabel = () => dispatch({ type: ActionTypes.ADD });
  const deleteLabel = (id: string) =>
    dispatch({ type: ActionTypes.DELETE, payload: id });
  const resetLabels = () => dispatch({ type: ActionTypes.RESET });

  const context = useMemo(
    () => ({
      labels: state.labels,
      isKeyInvalidFormat: state.isKeyInvalidFormat,
      isDuplicated: state.isDuplicated,
      isValueEmpty: state.isValueEmpty,
      loadLabels,
      updateLabelKey,
      updateLabelValue,
      addLabel,
      deleteLabel,
      resetLabels,
    }),
    [state],
  );

  return (
    <LabelsContext.Provider value={context}>
      {props.children}
    </LabelsContext.Provider>
  );
}
